import { useCallback, useState } from "react";
import { useNotify } from "ra-core";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { streamSkillRun } from "@/lib/agenticClient";

// Sprint 10.x — Flow commun des cards "Outils IA" : 1 clic → skill (SSE)
// → liste d'approvals → l'utilisateur voit / édite / valide / refuse chacune.
// Utilisé par AgentApprovalBatchCard (générique) et RelanceNewSignupsCard.

export type ApprovalRow = {
  approval_id: string;
  contact_label: string;
  subtitle: string;
  state: "pending" | "applying" | "done" | "rejecting" | "rejected" | "error";
  error?: string;
  expanded?: boolean;
  body?: string;
  bodyLoading?: boolean;
  bodyError?: string;
  recipient?: string;
  // Édition inline (subject = subtitle, body = main text)
  editing?: boolean;
  editedSubtitle?: string;
  editedBody?: string;
  saving?: boolean;
  saveError?: string;
};

export type RunState = "idle" | "running" | "done" | "error";

type SkillOutput = { summary_markdown?: string } & Record<string, unknown>;

export function useAgenticApprovalRun({
  extractBody,
  applyEdits,
  approveSuccessLabel = "Validé",
  doneLabel = "action(s) à valider",
}: {
  /** Extrait le texte principal (et éventuels champs) du payload. */
  extractBody: (payload: Record<string, unknown>) => Partial<
    Pick<ApprovalRow, "body" | "subtitle" | "recipient">
  > & {
    body: string;
  };
  /** Réinjecte les edits dans le payload avant sauvegarde. */
  applyEdits: (
    payload: Record<string, unknown>,
    editedSubtitle: string,
    editedBody: string,
  ) => Record<string, unknown>;
  approveSuccessLabel?: string;
  doneLabel?: string;
}) {
  const notify = useNotify();
  const [runState, setRunState] = useState<RunState>("idle");
  const [progress, setProgress] = useState(0);
  const [approvals, setApprovals] = useState<ApprovalRow[]>([]);
  const [runError, setRunError] = useState<string | null>(null);
  const [summaryMd, setSummaryMd] = useState<string | null>(null);

  const patch = useCallback(
    (approval_id: string, p: Partial<ApprovalRow>) =>
      setApprovals((prev) =>
        prev.map((x) => (x.approval_id === approval_id ? { ...x, ...p } : x)),
      ),
    [],
  );

  /**
   * Lance le skill. `before` tourne avant le stream (ex: annuler les drafts
   * précédents) ; `toRows` transforme l'output final en lignes.
   */
  const launch = useCallback(
    async (
      skillId: string,
      skillInput: Record<string, unknown>,
      toRows: (
        out: SkillOutput,
        ctx: { runId: number | null; approvalsCreated: number },
      ) => Promise<ApprovalRow[]> | ApprovalRow[],
      before?: () => Promise<void>,
    ) => {
      setRunState("running");
      setProgress(0);
      setApprovals([]);
      setRunError(null);
      setSummaryMd(null);
      try {
        await before?.();
        let approvalsCreated = 0;
        let runId: number | null = null;
        for await (const ev of streamSkillRun(skillId, skillInput)) {
          if (ev.event === "run.started") {
            runId = (ev.data as { run_id?: number }).run_id ?? null;
          }
          if (
            ev.event === "tool_use" &&
            (ev.data as { name?: string }).name === "request_approval"
          ) {
            approvalsCreated++;
            setProgress(approvalsCreated);
          }
          if (ev.event === "run.done") {
            const out = (ev.data.output ?? {}) as SkillOutput;
            const list = await toRows(out, { runId, approvalsCreated });
            setApprovals(list);
            setSummaryMd(out.summary_markdown ?? null);
            setRunState("done");
            notify(`${list.length} ${doneLabel}`, { type: "success" });
          }
          if (ev.event === "run.error") {
            throw new Error(ev.data.error);
          }
        }
      } catch (e) {
        setRunError(e instanceof Error ? e.message : String(e));
        setRunState("error");
      }
    },
    [notify, doneLabel],
  );

  const toggleBody = useCallback(
    async (a: ApprovalRow) => {
      patch(a.approval_id, { expanded: !a.expanded });
      if (a.body || a.expanded) return;
      patch(a.approval_id, { bodyLoading: true, bodyError: undefined });
      try {
        const { data, error } = await getSupabaseClient()
          .from("agentic_pending_approvals")
          .select("payload")
          .eq("id", a.approval_id)
          .single();
        if (error) throw error;
        const payload =
          (data as { payload: Record<string, unknown> }).payload ?? {};
        const { subtitle, ...rest } = extractBody(payload);
        patch(a.approval_id, {
          ...rest,
          ...(subtitle !== undefined && { subtitle }),
          bodyLoading: false,
        });
      } catch (e) {
        patch(a.approval_id, {
          bodyError: e instanceof Error ? e.message : String(e),
          bodyLoading: false,
        });
      }
    },
    [extractBody, patch],
  );

  const startEdit = useCallback(
    (a: ApprovalRow) =>
      patch(a.approval_id, {
        expanded: true,
        editing: true,
        editedSubtitle: a.subtitle,
        editedBody: a.body ?? "",
        saveError: undefined,
      }),
    [patch],
  );

  const cancelEdit = useCallback(
    (a: ApprovalRow) =>
      patch(a.approval_id, {
        editing: false,
        editedSubtitle: undefined,
        editedBody: undefined,
        saveError: undefined,
      }),
    [patch],
  );

  const updateEdit = useCallback(
    (approval_id: string, field: "subtitle" | "body", value: string) =>
      patch(
        approval_id,
        field === "subtitle"
          ? { editedSubtitle: value }
          : { editedBody: value },
      ),
    [patch],
  );

  const saveEdit = useCallback(
    async (a: ApprovalRow) => {
      const newSubtitle = (a.editedSubtitle ?? "").trim();
      const newBody = a.editedBody ?? "";
      patch(a.approval_id, { saving: true, saveError: undefined });
      try {
        const supabase = getSupabaseClient();
        // Récupère le payload courant pour préserver les autres clés.
        const { data, error } = await supabase
          .from("agentic_pending_approvals")
          .select("payload")
          .eq("id", a.approval_id)
          .single();
        if (error) throw error;
        const payload =
          (data as { payload: Record<string, unknown> }).payload ?? {};
        const { error: uErr } = await supabase
          .from("agentic_pending_approvals")
          .update({ payload: applyEdits(payload, newSubtitle, newBody) })
          .eq("id", a.approval_id);
        if (uErr) throw uErr;
        patch(a.approval_id, {
          subtitle: newSubtitle || a.subtitle,
          body: newBody,
          editing: false,
          saving: false,
          editedBody: undefined,
          editedSubtitle: undefined,
        });
        notify("Modifications enregistrées", { type: "info" });
      } catch (e) {
        patch(a.approval_id, {
          saving: false,
          saveError: e instanceof Error ? e.message : String(e),
        });
      }
    },
    [applyEdits, notify, patch],
  );

  const decide = useCallback(
    async (a: ApprovalRow, action: "execute" | "reject") => {
      patch(a.approval_id, {
        state: action === "execute" ? "applying" : "rejecting",
      });
      try {
        const {
          data: { session },
        } = await getSupabaseClient().auth.getSession();
        if (!session) throw new Error("not_authenticated");
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-runtime/approvals/${a.approval_id}/${action}`;
        const res = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
        });
        if (!res.ok && res.status !== 409) {
          const body = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
        }
        patch(a.approval_id, {
          state: action === "execute" ? "done" : "rejected",
        });
        notify(action === "execute" ? approveSuccessLabel : "Refusé", {
          type: action === "execute" ? "success" : "info",
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        patch(a.approval_id, { state: "error", error: msg });
        notify(`Erreur : ${msg}`, { type: "error" });
      }
    },
    [notify, approveSuccessLabel, patch],
  );

  return {
    runState,
    setRunState,
    progress,
    approvals,
    setApprovals,
    runError,
    summaryMd,
    patch,
    launch,
    toggleBody,
    startEdit,
    cancelEdit,
    updateEdit,
    saveEdit,
    decide,
  };
}
